/**
 * # as-debug-project-view
 *
 * Debug panel that displays current project state from the store
 * and live project data. Intended for the inspector's Debug accordion.
 *
 * Auto-refreshes on store-update events and periodically.
 */

import { liveAppData } from '../../store/store.ts';
import { currentProjectData, isProjectUnsaved } from '../../data/projectManagement.ts';

const STYLE = /*css*/`
  :host {
    display: block;
    font-family: var(--as-font-family-mono, monospace);
    font-size: 0.75rem;
    color: var(--color-text-primary, #333);
    padding: 0.5rem;
  }
  .section-label {
    font-weight: 600;
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--color-text-secondary, #888);
    margin: 0.5rem 0 0.25rem;
  }
  .section-label:first-child { margin-top: 0; }
  table {
    width: 100%;
    border-collapse: collapse;
  }
  td {
    padding: 0.15rem 0.35rem;
    vertical-align: top;
    border-bottom: 1px solid var(--color-border-muted, #eee);
  }
  td:first-child {
    white-space: nowrap;
    color: var(--color-text-secondary, #888);
    width: 1%;
  }
  td:last-child {
    word-break: break-all;
  }
  .none { color: var(--color-text-secondary, #aaa); font-style: italic; }
  .badge {
    display: inline-block;
    padding: 0.1rem 0.35rem;
    border-radius: 0.25rem;
    font-size: 0.65rem;
    font-weight: 600;
  }
  .badge-unsaved { background: #fff3cd; color: #856404; }
  .badge-saved { background: #d4edda; color: #155724; }
  .capture-btn {
    font: inherit;
    font-size: 0.65rem;
    padding: 0.1rem 0.3rem;
    border: 1px solid var(--color-border-muted, #ccc);
    border-radius: 0.2rem;
    background: var(--color-bg-primary, #fff);
    cursor: pointer;
    vertical-align: middle;
    margin-left: 0.35rem;
  }
  .capture-btn:hover { background: var(--color-bg-secondary, #f0f0f0); }
  .json-preview {
    max-height: 6rem;
    overflow-y: auto;
    background: var(--color-bg-secondary, #f5f5f5);
    padding: 0.25rem 0.35rem;
    border-radius: 0.2rem;
    white-space: pre-wrap;
    word-break: break-all;
  }
`;

export class AsDebugProjectView extends HTMLElement {
  private static readonly sheet = (() => {
    const s = new CSSStyleSheet();
    s.replaceSync(STYLE);
    return s;
  })();

  private boundRefresh: () => void;

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [AsDebugProjectView.sheet];
    this.boundRefresh = () => this.refresh();
  }

  connectedCallback() {
    window.addEventListener('store-update', this.boundRefresh);
    this.refresh();
  }

  disconnectedCallback() {
    window.removeEventListener('store-update', this.boundRefresh);
  }

  private async captureSession() {
    const cfgr = document.querySelector('as-configurator') as any;
    if (!cfgr?.storeSession) return;
    const btn = this.shadowRoot!.getElementById('capture-session');
    if (btn) btn.textContent = '…';
    try {
      await cfgr.storeSession();
    } finally {
      this.refresh();
    }
  }

  private refresh() {
    const proj = liveAppData.currentProject;
    const cpd = currentProjectData;

    if (!proj) {
      this.shadowRoot!.innerHTML = `<span class="none">No project loaded</span>`;
      return;
    }

    const m = proj.metadata;
    const unsaved = isProjectUnsaved(proj);
    const fmtDate = (d?: Date) => d ? new Date(d).toLocaleString() : '--';
    const liveParamValues = liveAppData.uiInputs ?? {};
    const liveParamCount = Object.keys(liveParamValues).length;
    const savedParamCount = Object.keys(proj.uiInputs ?? {}).length;
    const meshCount = (proj.outputMeshes ?? []).length;
    const customDataKeys = Object.keys(proj.customData ?? {}).length;
    const sessionBlob = proj.paramateSession;
    const sessionKB = sessionBlob ? (sessionBlob.size / 1024).toFixed(1) : null;
    const cpdParamKeys = Object.keys(liveParamValues);
    const cpdMeshKeys = Object.keys(cpd.meshes ?? {});

    // Preserve scroll position across redraws
    const container = this.shadowRoot!.firstElementChild as HTMLElement | null;
    const scrollTop = container?.scrollTop ?? 0;

    this.shadowRoot!.innerHTML = /*html*/`
      <div class="section-label">Project Metadata</div>
      <table>
        <tr><td>name</td><td>${m.name} <span class="badge ${unsaved ? 'badge-unsaved' : 'badge-saved'}">${unsaved ? 'unsaved' : 'saved'}</span></td></tr>
        <tr><td>id</td><td>${m.id}</td></tr>
        <tr><td>appId</td><td>${m.appId}</td></tr>
        <tr><td>created</td><td>${fmtDate(m.createdAt)}</td></tr>
        <tr><td>updated</td><td>${fmtDate(m.updatedAt)}</td></tr>
        <tr><td>saved</td><td>${fmtDate(m.savedAt)}</td></tr>
      </table>

      <div class="section-label">Paramate Session</div>
      <table>
        <tr><td>session ID</td><td>${cpd.sessionId || '<span class="none">not initialized</span>'}</td></tr>
        <tr><td>session zip</td><td>${sessionKB ? `${sessionKB} KB` : '<span class="none">not captured</span>'}<button class="capture-btn" id="capture-session">&#8635;</button></td></tr>
      </table>

      <div class="section-label">Project Contents</div>
      <table>
        <tr><td>uiInputs (live)</td><td>${liveParamCount} inputs</td></tr>
        <tr><td>uiInputs (saved)</td><td>${savedParamCount}</td></tr>
        <tr><td>outputMeshes</td><td>${meshCount}</td></tr>
        <tr><td>customData</td><td>${customDataKeys} keys</td></tr>
      </table>

      <div class="section-label">Live State (currentProjectData)</div>
      <table>
        <tr><td>uiInputs</td><td>${cpdParamKeys.length} keys</td></tr>
        <tr><td>meshes</td><td>${cpdMeshKeys.length} entries</td></tr>
        <tr><td>customData</td><td>${Object.keys(cpd.customData ?? {}).length} keys</td></tr>
      </table>

      ${liveParamCount > 0 ? `
        <div class="section-label">uiInputs (live)</div>
        <div class="json-preview">${JSON.stringify(liveParamValues, null, 1)}</div>
      ` : ''}
      ${savedParamCount > 0 ? `
        <div class="section-label">uiInputs (saved)</div>
        <div class="json-preview">${JSON.stringify(proj.uiInputs, null, 1)}</div>
      ` : ''}
    `;

    // Restore scroll position
    const newContainer = this.shadowRoot!.firstElementChild as HTMLElement | null;
    if (newContainer) newContainer.scrollTop = scrollTop;

    this.shadowRoot!.getElementById('capture-session')?.addEventListener('click', () => this.captureSession());
  }
}

if (!customElements.get('as-debug-project-view')) {
  customElements.define('as-debug-project-view', AsDebugProjectView);
}
